/**
 * @file InteractiveMenu.jsx
 * @description Menú interactivo con categorías, búsqueda y tarjetas animadas.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { menuData } from '../data/menuData';

const InteractiveMenu = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [language, setLanguage] = useState('es');

  useEffect(() => {
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang.startsWith('en')) {
      setLanguage('en');
    }
  }, []);

  const filteredCategories = useMemo(() => {
    if (activeCategory === 'all') {
      return menuData;
    }
    return menuData.filter((cat) => cat.id === activeCategory);
  }, [activeCategory]);

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return filteredCategories;
    const q = searchQuery.toLowerCase();
    return filteredCategories
      .map((cat) => ({
        ...cat,
        items: cat.items.filter(
          (item) => {
            if (language === 'en') {
              return (item.name_en?.toLowerCase() || '').includes(q) ||
                     (item.description_en?.toLowerCase() || '').includes(q) ||
                     item.name.toLowerCase().includes(q);
            }
            return item.name.toLowerCase().includes(q) ||
                   item.description.toLowerCase().includes(q);
          }
        ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [filteredCategories, searchQuery, language]);

  return (
    <section id="menu" className="interactive-menu-section">
      <div className="menu-hero">
        <div className="language-toggle" style={{ textAlign: 'right', marginBottom: '1rem', paddingRight: '2rem' }}>
          <button 
            onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
            className="menu-tab"
            style={{ padding: '0.5rem 1rem', fontSize: '0.9rem', cursor: 'pointer' }}
          >
            {language === 'es' ? 'Switch to English' : 'Ver en Español'}
          </button>
        </div>
        <h2 className="cormorant-garamond menu-main-title">
          {language === 'en' ? 'Our Menu' : 'Nuestra Carta'}
        </h2>
        <p className="menu-subtitle">
          {language === 'en' 
            ? 'Authentic flavors of Peruvian cuisine, prepared with fresh ingredients'
            : 'Sabores auténticos de la gastronomía peruana, preparados con ingredientes frescos'}
        </p>
      </div>

      {/* Search Bar */}
      <div className="menu-search-wrapper">
        <div className="menu-search-box">
          <span className="search-icon">🔍</span>
          <input
            id="menu-search"
            type="text"
            placeholder={language === 'en' ? 'Search for a dish...' : 'Buscar un plato...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="menu-search-input"
            aria-label={language === 'en' ? 'Search dishes in the menu' : 'Buscar platos en el menú'}
          />
          {searchQuery && (
            <button
              className="search-clear"
              onClick={() => setSearchQuery('')}
              aria-label={language === 'en' ? 'Clear search' : 'Limpiar búsqueda'}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Category Tabs */}
      <div className="menu-tabs-wrapper">
        <div className="menu-tabs" role="tablist" aria-label="Categorías del menú">
          <button
            role="tab"
            id="tab-all"
            aria-selected={activeCategory === 'all'}
            className={`menu-tab ${activeCategory === 'all' ? 'active' : ''}`}
            onClick={() => setActiveCategory('all')}
          >
            🍽️ {language === 'en' ? 'All' : 'Todo'}
          </button>
          {menuData.map((cat) => (
            <button
              key={cat.id}
              role="tab"
              id={`tab-${cat.id}`}
              aria-selected={activeCategory === cat.id}
              className={`menu-tab ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.icon} {language === 'en' ? cat.label_en : cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Menu Content */}
      <div className="menu-content">
        {filteredData.length === 0 ? (
          <div className="menu-no-results">
            <span>😕</span>
            <p>
              {language === 'en' ? 'No dishes found for' : 'No se encontraron platos para'} "<strong>{searchQuery}</strong>"
            </p>
          </div>
        ) : (
          filteredData.map((category) => (
            <div key={category.id} className="menu-category-block">
              <div className="menu-category-header">
                <span className="menu-category-icon">{category.icon}</span>
                <h3 className="menu-category-title cormorant-garamond">
                  {language === 'en' ? category.label_en : category.label}
                </h3>
                <div className="menu-category-divider" />
              </div>

              <div className="menu-items-grid">
                {category.items.map((item, idx) => (
                  <div
                    key={`${category.id}-${idx}`}
                    className="menu-item-card"
                    role="article"
                    aria-label={`${item.name}, S/ ${item.price}`}
                  >
                    <div className="menu-item-body">
                      <div className="menu-item-name-row">
                        <div className="menu-item-titles" style={{ flex: 1, paddingRight: '1rem' }}>
                          <h4 className="menu-item-name">{item.name}</h4>
                          {language === 'en' && item.name_en && (
                            <span className="menu-item-name-en" style={{ display: 'block', fontSize: '0.9rem', color: 'var(--text-secondary, #b3b3b3)', fontStyle: 'italic', marginTop: '0.2rem' }}>
                              {item.name_en}
                            </span>
                          )}
                        </div>
                        <span className="menu-item-price">S/ {item.price}</span>
                      </div>
                      {(language === 'en' && item.description_en) ? (
                        <p className="menu-item-description" style={{ marginTop: '0.5rem' }}>{item.description_en}</p>
                      ) : item.description ? (
                        <p className="menu-item-description" style={{ marginTop: '0.5rem' }}>{item.description}</p>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
};

export default InteractiveMenu;
